import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ticketAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const primary = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';

  const parseFilter = (search) => {
    const p = new URLSearchParams(search);
    return {
      status: p.get('status') || '',
      priority: p.get('priority') || '',
      date_from: p.get('date_from') || '',
      date_to: p.get('date_to') || '',
      sub_dist: p.get('sub_dist') || '',
      sla_breached: p.get('sla_breached') || '',
      created_by: p.get('created_by') || '',
      assigned_to: p.get('assigned_to') || '',
      page: 1
    };
  };

  const [filter, setFilter] = useState(() => parseFilter(location.search));
  const [staffUsers, setStaffUsers] = useState([]);

  useEffect(() => {
    fetch('/api/users?limit=100', {
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
    }).then(r => r.json()).then(d => {
      if (d.success) setStaffUsers(d.users || []);
    }).catch(() => {});
  }, []);

  const isSubDistAdmin = user?.role === 'subdist_admin';
  const creatorRoles = ['dist_l1','dist_l2','dist_l3','subdist_admin','crm_staff','dist_head','isp_admin'];
  const resolverRoles = ['subdist_tech_own','subdist_tech_isp','isp_technician','area_engineer','dist_l1','dist_l2','dist_l3'];
  const creators = staffUsers.filter(u => creatorRoles.includes(u.role));
  const resolvers = staffUsers.filter(u => resolverRoles.includes(u.role));

  useEffect(() => {
    setFilter(parseFilter(location.search));
  }, [location.search]);

  useEffect(() => {
    setLoading(true);
    const params = { ...filter };
    if (!params.status) delete params.status;
    if (!params.priority) delete params.priority;
    if (!params.date_from) delete params.date_from;
    if (!params.date_to) delete params.date_to;
    if (!params.sub_dist) delete params.sub_dist;
    if (!params.sla_breached) delete params.sla_breached;
    if (!params.created_by) delete params.created_by;
    if (!params.assigned_to) delete params.assigned_to;
    ticketAPI.getAll(params).then(res => {
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    }).catch(() => { setTickets([]); setTotal(0); }).finally(() => setLoading(false));
  }, [filter]);

  const statusColor = { open: '#2196f3', assigned: '#9c27b0', in_progress: '#ff9800', escalated: '#f44336', resolved: '#4caf50', closed: '#757575', reopened: '#e91e63' };
  const priorityColor = { high: '#f44336', critical: '#b71c1c', medium: '#ff9800', low: '#4caf50' };

  const clearFilter = () => setFilter({ status: '', priority: '', date_from: '', date_to: '', sub_dist: '', sla_breached: '', created_by: '', assigned_to: '', page: 1 });

  const getFilterLabel = () => {
    const parts = [];
    if (filter.status) parts.push('Status: ' + filter.status);
    if (filter.priority) parts.push('Priority: ' + filter.priority);
    if (filter.date_from) parts.push('From: ' + filter.date_from);
    if (filter.date_to) parts.push('To: ' + filter.date_to);
    if (filter.sub_dist) parts.push('Sub-dist: ' + filter.sub_dist);
    if (filter.sla_breached) parts.push('SLA Breached');
    if (filter.created_by) { const u = staffUsers.find(u => u._id === filter.created_by); parts.push('By: ' + (u ? u.name : filter.created_by)); }
    if (filter.assigned_to) { const u = staffUsers.find(u => u._id === filter.assigned_to); parts.push('Assigned: ' + (u ? u.name : filter.assigned_to)); }
    return parts.length ? parts.join(' | ') : 'All Tickets';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', color: '#333' }}>Tickets ({total})</h2>
          <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>{getFilterLabel()}</p>
        </div>
        {user && ['superadmin','isp_admin','dist_head','dist_l1','dist_l2','dist_l3','subdist_tech_own','subdist_tech_isp','isp_technician'].includes(user.role) && (
          <button onClick={() => navigate('/tickets/new')} style={{ background: primary, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>+ New Ticket</button>
        )}
      </div>

      {/* Filters */}
      <div style={{ background: 'white', padding: '16px', borderRadius: '10px', marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filter.status} onChange={e => setFilter({...filter, status: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In Progress</option>
          <option value="escalated">Escalated</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
          <option value="open,assigned,in_progress,escalated">All Pending</option>
        </select>
        <select value={filter.priority} onChange={e => setFilter({...filter, priority: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">All Priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
{!isSubDistAdmin && (        <select value={filter.sub_dist} onChange={e => setFilter({...filter, sub_dist: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">All Sub-Dist</option>
          <option value="KB13A">KB13A - IFB IRRITY</option>
          <option value="KB13B">KB13B - PCI PAYYANUR</option>
          <option value="KB13C">KB13C - C2NET</option>
          <option value="KB13D">KB13D - CITYVISION</option>
          <option value="KB13E">KB13E - GRAMIKA</option>
          <option value="KB13F">KB13F - CCNET</option>
          <option value="KB13H">KB13H - MALABAR</option>
          <option value="KB13J">KB13J - MAYYAZHI</option>
        </select>)}}
        <select value={filter.created_by||''} onChange={e => setFilter({...filter, created_by: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">Created By (All)</option>
          <optgroup label="PDIC Staff">
            {creators.filter(u=>['dist_l1','dist_l2','dist_l3','dist_head','isp_admin'].includes(u.role)).map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </optgroup>
          <optgroup label="Sub-Dist Admin">
            {creators.filter(u=>u.role==='subdist_admin').map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </optgroup>
          <optgroup label="CRM Staff">
            {creators.filter(u=>u.role==='crm_staff').map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </optgroup>
        </select>
        <select value={filter.assigned_to||''} onChange={e => setFilter({...filter, assigned_to: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">Assigned To (All)</option>
          <optgroup label="PDIC Technicians">
            {resolvers.filter(u=>['dist_l1','dist_l2','dist_l3'].includes(u.role)).map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </optgroup>
          <optgroup label="Sub-Dist Technicians">
            {resolvers.filter(u=>['subdist_tech_own','subdist_tech_isp','isp_technician','area_engineer'].includes(u.role)).map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </optgroup>
        </select>
        <input type="date" value={filter.date_from||''} onChange={e => setFilter({...filter, date_from: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }} title="From date" />
        <input type="date" value={filter.date_to||''} onChange={e => setFilter({...filter, date_to: e.target.value, page: 1})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }} title="To date" />
        <button onClick={clearFilter} style={{ padding: '8px 16px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>✕ Clear</button>
      </div>

      {/* Ticket Table */}
      <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <p style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading tickets...</p>
        ) : tickets.length === 0 ? (
          <p style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No tickets found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Ticket #</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Customer</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Sub-Dist</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Issue</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Priority</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Technician</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Created</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t._id} onClick={() => navigate('/tickets/' + t._id)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}>
                  <td style={{ padding: '12px', fontSize: '13px', fontWeight: '700', color: primary }}>{t.ticket_number}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{t.customer?.name || 'N/A'}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.customer?.phone || ''}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{t.customer?.connection_id || ''}</div>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>{t.customer?.sub_dist_code || 'N/A'}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontSize: '13px', color: '#334155' }}>{t.complaint?.category || 'N/A'}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.complaint?.description || ''}</div>
                  </td>
                  <td style={{ padding: '12px' }}><span style={{ background: (statusColor[t.status]||'#gray') + '20', color: statusColor[t.status]||'#666', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>{t.status}</span></td>
                  <td style={{ padding: '12px' }}><span style={{ color: priorityColor[t.complaint?.priority]||'#666', fontSize: '12px', fontWeight: '600' }}>{(t.complaint?.priority||'').toUpperCase()}</span></td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#475569' }}>
                    <div>{t.assigned_to?.name || 'Unassigned'}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.assigned_to?.role || ''}</div>
                  </td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                    <div>{new Date(t.createdAt).toLocaleDateString('en-IN')}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(t.createdAt).toLocaleTimeString('en-IN', {hour:'2-digit',minute:'2-digit'})}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.created_by?.name || ''}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {t.source === 'public_url' ? <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>🌐 Online</span>
                    : t.source === 'whatsapp' ? <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>💬 WhatsApp</span>
                    : <span style={{ background: '#f3e5f5', color: '#7b1fa2', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>💻 Staff</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
        <button disabled={filter.page === 1} onClick={() => setFilter({...filter, page: filter.page - 1})} style={{ padding: '8px 20px', border: '1px solid #ddd', borderRadius: '6px', cursor: filter.page === 1 ? 'not-allowed' : 'pointer', background: filter.page === 1 ? '#f5f5f5' : 'white' }}>← Previous</button>
        <span style={{ padding: '8px 16px', color: '#666', fontSize: '14px' }}>Page {filter.page}</span>
        <button onClick={() => setFilter({...filter, page: filter.page + 1})} style={{ padding: '8px 20px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', background: 'white' }}>Next →</button>
      </div>
    </div>
  );
};

export default TicketList;
